import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import { isNativeApp } from "@/lib/platform";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Browse from "@/pages/Browse";
import CreateListing from "@/pages/CreateListing";
import RoomDetail from "@/pages/RoomDetail";
import Matches from "@/pages/Matches";
import ExchangeBuddy from "@/pages/ExchangeBuddy";
import EditProfile from "@/pages/EditProfile";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding, { hasSeenOnboarding } from "@/pages/Onboarding";
import Messages from "@/pages/Messages";
import Admin from "@/pages/Admin";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import TosGate from "@/components/TosGate";
import Footer from "@/components/Footer";
import BottomTabBar from "@/components/mobile/BottomTabBar";
import NewMatchPopup from "@/components/NewMatchPopup";
import CookieConsent from "@/components/CookieConsent";
// Push notifications are disabled until Firebase is configured on Android.
// See /app/frontend/src/lib/pushNotifications.js for the re-enable checklist.

// Auto-scrolls to top on every route change.
function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
    }, [pathname]);
    return null;
}

// Removes the static pre-hydration splash injected in index.html once React
// has taken over. This guarantees no white/black flash at boot: the splash
// paints instantly (it's static HTML), then fades out after the app renders.
function useHideStaticSplash() {
    useEffect(() => {
        const remove = () => {
            const el = document.getElementById("app-splash-static");
            if (!el) return;
            el.classList.add("hide");
            // Restore the real app background once the splash starts fading.
            // (index.html paints the html/body in the splash's terracotta so
            // the very first frame is never white/black.)
            document.documentElement.style.backgroundColor = "";
            document.body.style.backgroundColor = "";
            setTimeout(() => { el.remove(); }, 400);
        };
        const t = setTimeout(remove, isNativeApp() ? 900 : 300);
        return () => clearTimeout(t);
    }, []);
}

// The "/" route target — differs between web (marketing landing) and app
// (dashboard for logged-in users, browse otherwise, onboarding on first launch).
function HomeRoute() {
    const { user, loading } = useAuth();
    if (isNativeApp()) {
        if (!hasSeenOnboarding()) return <Navigate to="/onboarding" replace />;
        if (loading) return null;
        return <Navigate to={user ? "/dashboard" : "/browse"} replace />;
    }
    return <Landing />;
}

function Shell() {
    const loc = useLocation();
    const isOnboarding = loc.pathname === "/onboarding";
    const bootRedirect = loc.pathname === "/" && isNativeApp();
    if (isOnboarding || bootRedirect) return <Outlet />;
    return (
        <>
            <Navbar />
            <main className={isNativeApp() ? "pb-[calc(72px+env(safe-area-inset-bottom))]" : ""}>
                <Outlet />
            </main>
            {!isNativeApp() && <Footer />}
            <BottomTabBar />
        </>
    );
}

function AppRoutes() {
    useHideStaticSplash();
    return (
        <BrowserRouter>
            <ScrollToTop />
            <NewMatchPopup />
            <TosGate />
            <CookieConsent />
            <Routes>
                <Route element={<Shell />}>
                    <Route path="/onboarding" element={<Onboarding />} />
                    <Route path="/" element={<HomeRoute />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/browse" element={<Browse />} />
                    <Route path="/exchange-buddy" element={<ExchangeBuddy />} />
                    <Route path="/rooms/:id" element={<RoomDetail />} />
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
                    <Route path="/listings/new" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
                    <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
                    <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                    <Route path="/messages/:userId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                    <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
}

export default function App() {
    return (
        <div className="App">
            <AuthProvider>
                <AppRoutes />
                <Toaster richColors position="top-right" />
            </AuthProvider>
        </div>
    );
}
